/**
 * Made-up candles so the Backtest page has data the moment it opens (and works offline).
 * Deterministic: the same options always give the same candles, so results are repeatable.
 * The price wanders in trending and ranging spells with changing volatility, roughly like a
 * major FX pair on 1-hour bars. It is NOT market data: real results need "Load candles".
 */
export function makeSampleCandles({
  count = 3000,
  tfSeconds = 3600,
  startPrice = 1.08,
  endTime = Math.floor(Date.UTC(2026, 9, 30, 21) / 1000),
  seed = 7,
  decimals = 5,
} = {}) {
  let state = seed >>> 0;
  const rand = () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = Math.imul(state ^ (state >>> 15), 1 | state);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
  const gauss = () => {
    const u = Math.max(rand(), 1e-12);
    return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * rand());
  };
  const round = (v) => Number(v.toFixed(decimals));

  const out = [];
  let price = startPrice;
  let drift = 0;
  let vol = 0.0012; // per-bar volatility as a fraction of price
  let spellLeft = 0;
  const firstTime = endTime - (count - 1) * tfSeconds;

  for (let i = 0; i < count; i += 1) {
    if (spellLeft <= 0) {
      // new spell: trend up, trend down or range, lasting 60 to 300 bars
      const kind = rand();
      drift = kind < 0.3 ? 0.00004 : kind < 0.6 ? -0.00004 : 0;
      spellLeft = 60 + Math.floor(rand() * 240);
    }
    spellLeft -= 1;
    vol = Math.min(0.004, Math.max(0.0005, vol * (1 + 0.08 * gauss())));
    const open = price;
    const close = open * (1 + drift + vol * gauss());
    const wickUp = open * vol * Math.abs(gauss()) * 0.6;
    const wickDown = open * vol * Math.abs(gauss()) * 0.6;
    const high = Math.max(open, close) + wickUp;
    const low = Math.min(open, close) - wickDown;
    out.push({
      time: firstTime + i * tfSeconds,
      open: round(open),
      high: round(high),
      low: round(low),
      close: round(close),
      volume: Math.round(500 + rand() * 1500 * (vol / 0.0012)),
    });
    price = close;
  }
  return out;
}
